import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { FacetCombobox, FacetComboboxOption } from '@/components/ui/facet-combobox';
import { useI18n } from '@/hooks/useI18n';
import { ActivityType } from '@/lib/builder/models/activity.model';

export type LibraryOwnerScope = 'all' | 'user' | 'agency';

interface LibraryFiltersProps {
  typeOptions: FacetComboboxOption[];
  selectedActivityType: ActivityType | null;
  onActivityTypeChange: (type: ActivityType | null) => void;

  /**
   * Proprietari presenti fra le attività visibili. Assente dove il filtro non direbbe
   * nulla: in libreria personale le righe sono tutte dell'utente corrente.
   */
  ownerOptions?: FacetComboboxOption[];
  selectedOwnerId: string | null;
  onOwnerChange: (ownerId: string | null) => void;

  /** Ambito personale/agenzia: solo per l'admin, che vede le righe di tutti. */
  showScopeFilter?: boolean;
  ownerScope: LibraryOwnerScope;
  onOwnerScopeChange: (scope: LibraryOwnerScope) => void;

  search: string;
  onSearchChange: (value: string) => void;
}

/**
 * Riga filtri della libreria, con la stessa grammatica della dashboard pagamenti
 * (`PaymentsFilters`): prima le tendine, poi un tratto verticale, poi la ricerca libera.
 *
 * L'ordine non è estetico. Le tendine restringono per valore scelto da un elenco chiuso e
 * coprono i casi normali; la ricerca libera cerca testo ovunque ed è il ripiego di chi in
 * tendina non trova la voce. Il separatore dice che sono due modi diversi di restringere
 * senza doverci mettere un'etichetta sopra.
 */
export function LibraryFilters({
  typeOptions,
  selectedActivityType,
  onActivityTypeChange,
  ownerOptions,
  selectedOwnerId,
  onOwnerChange,
  showScopeFilter = false,
  ownerScope,
  onOwnerScopeChange,
  search,
  onSearchChange,
}: LibraryFiltersProps) {
  const { t } = useI18n();

  // Le liste sono complete (le attività sono già tutte in memoria, vedi LibraryPage):
  // nessuna ricerca lato server, cmdk filtra in memoria.
  const shared = {
    clearLabel: t('library.filtersClear'),
    emptyLabel: t('library.filtersNoOptions'),
    className: 'min-w-[160px]',
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <FacetCombobox
        {...shared}
        options={typeOptions}
        value={selectedActivityType}
        onChange={(value) => onActivityTypeChange(value as ActivityType | null)}
        placeholder={t('library.typeFilterAllTypesHint')}
        searchPlaceholder={t('library.typeFilterSearchPlaceholder')}
      />

      {ownerOptions && (
        <FacetCombobox
          {...shared}
          options={ownerOptions}
          value={selectedOwnerId}
          onChange={onOwnerChange}
          placeholder={t('library.ownerFilterAllOwners')}
          searchPlaceholder={t('library.ownerFilterSearchPlaceholder')}
        />
      )}

      {showScopeFilter && (
        // Ambito ≠ proprietario: qui si sceglie fra righe personali e righe di agenzia,
        // non fra persone. Sta dopo il proprietario perché è il filtro più raro.
        <FacetCombobox
          {...shared}
          options={[
            { value: 'user', label: t('library.ownerScopeUser') },
            { value: 'agency', label: t('library.ownerScopeAgency') },
          ]}
          value={ownerScope === 'all' ? null : ownerScope}
          onChange={(value) => onOwnerScopeChange((value as LibraryOwnerScope) ?? 'all')}
          placeholder={t('library.ownerScopeAll')}
          searchPlaceholder={t('library.ownerScopeLabel')}
        />
      )}

      <span className="w-px h-6 bg-gray-200 mx-1" aria-hidden="true" />

      <div className="relative w-[200px]">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <Input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t('library.tableSearchPlaceholder')}
          className="h-9 pl-8 text-sm"
        />
      </div>
    </div>
  );
}
